import React from "react";
import { Typography } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import StatusAssignerDropdown from "@/pages/explorer/components/statusAssignerDropdown/StatusAssignerDropdown";
import OwnerAssignerDropdown from "@/pages/explorer/components/ownerAssignerDropdown/OwnerAssignerDropdown";
import { BaseDetailsProps } from "../types";
import { InfoRow } from "./InfoRow";
import { Resources } from "./Resources";
import dayjs from "dayjs";
import EffortInfo from "./EffortInfo";
import MoneySavingsInfo from "./MoneySavingsInfo";
import OpportunityStatusTooltip from "./OpportunityStatusTooltip";

interface InformationTabProps extends BaseDetailsProps {}

export const InformationTab: React.FC<InformationTabProps> = ({
  opportunity,
  assignedStatus,
  setAssignedStatus,
  assignedOwner,
  setAssignedOwner,
  startDate,
  endDate,
  savingsInfo,
  extraContent,
}) => {
  return (
    <>
      <div className="al-space-y-4">
        <Stack gap="2xl">
          <InfoRow
            label="Status"
            value={
              <StatusAssignerDropdown
                opportunity_id={opportunity?.opportunity_id}
                assignedStatus={assignedStatus}
                setAssignedStatus={setAssignedStatus}
                startDate={startDate?.toISOString() || ""}
                endDate={endDate?.toISOString() || ""}
              />
            }
            tooltip={<OpportunityStatusTooltip assignedStatus={assignedStatus} />}
          />
          <InfoRow
            label="Owner"
            value={
              <OwnerAssignerDropdown
                opportunity_id={opportunity?.opportunity_id}
                assignedOwner={assignedOwner}
                setAssignedOwner={setAssignedOwner}
                startDate={startDate?.toISOString() || ""}
                endDate={endDate?.toISOString() || ""}
              />
            }
            tooltip="Owner assigned to this opportunity."
          />
        </Stack>
        <div className="al-grid al-grid-cols-2 md:al-grid-cols-3 lg:al-grid-cols-5 al-gap-y-3">
          <EffortInfo opportunity={opportunity} />
          <InfoRow
            label="First Detected"
            value={
              <Typography variant="body" size="sm">
                {opportunity.first_seen ? `${dayjs(opportunity.first_seen).fromNow()}` : "N/A"}
              </Typography>
            }
            tooltip={`This opportunity was first seen on ${dayjs(opportunity.first_seen).format("MMM D, YYYY h:mm A")}.`}
          />
          <InfoRow
            label="Last Detected"
            value={
              <Typography variant="body" size="sm">
                {opportunity.last_seen ? `${dayjs(opportunity.last_seen).fromNow()}` : "N/A"}
              </Typography>
            }
            tooltip={`This opportunity was last seen on ${dayjs(opportunity.last_seen).format("MMM D, YYYY h:mm A")}.`}
          />
          <InfoRow
            label="Time Savings"
            value={
              <Typography variant="body" size="sm" weight="medium">
                {opportunity.time_savings || "N/A"}
              </Typography>
            }
            tooltip={`Approximate time saved after implementing this opportunity between ${dayjs(startDate).format("MMM D, YYYY")} - ${dayjs(endDate).format("MMM D, YYYY")}.`}
          />
          <MoneySavingsInfo opportunity={opportunity} savingsInfo={savingsInfo} />
          <InfoRow
            label="Count"
            value={
              <Typography variant="body" size="sm" weight="medium">
                {opportunity.count || 0}
              </Typography>
            }
            tooltip={`This opportunity has been detected ${opportunity.count || 0} times between ${dayjs(startDate).format("MMM D, YYYY")} - ${dayjs(endDate).format("MMM D, YYYY")}.`}
          />

          {extraContent &&
            Object.keys(extraContent).length > 0 &&
            Object.entries(extraContent).map(([label, value], index) => (
              <InfoRow key={index} label={label} value={value} />
            ))}
        </div>
      </div>

      {/* Resources Section */}
      {opportunity?.resources && opportunity.resources.length > 0 && (
        <Stack className="al-mt-4" direction="column">
          <Typography variant="body" weight="medium">
            Resources
          </Typography>
          <Stack>
            <Resources
              resources={opportunity.resources}
              instance_id={String(opportunity.instance_id)}
              opportunity_id={opportunity.opportunity_id}
              startDate={startDate}
              endDate={endDate}
            />
          </Stack>
        </Stack>
      )}
    </>
  );
};
